import React, { useRef, useEffect, useImperativeHandle } from 'react';
import { PropTypes } from 'prop-types';
import Icon from '../../Icons/Icon';
import { capitalizeFirstLetter } from '../../../utils/api';

const TextInput = React.forwardRef(
  (
    {
      name,
      label = undefined,
      labelTextSize = undefined,
      value = undefined,
      setValue = () => {},
      type = 'text',
      isRequired = false,
      asteriskWidth = undefined,
      errorMessage = undefined, // requires isRequired to be true if need to show validation error use validationError instead
      onKeyEnterPressed = undefined,
      placeholder = undefined,
      isValid = true,
      className = undefined,
      style = undefined,
      borderColor = 'border-vamos-blue-gray',
      isLabelBeside = false,
      customErrorComponent = null,
      customLabelClasses = undefined,
      customInputClasses = undefined,
      customIcon = null,
      spaceY = undefined, // string of space classes. ex: 'space-y-1'
      fontSize = undefined,
      inputHeight = undefined,
      labelBesideLabelClasses = undefined,
      labelBesideInputClasses = undefined,
      isDisabled = false,
      validationError = undefined,
      errorMessageClass = undefined, // you can pass 'absolute' to make it absolute positioned
      defaultValue = undefined,
      isAlertIcon = false,
      errorMessageFontClasses = undefined,
      errorMessageZIndexClass = 'z-20',
      inputTextPadding = undefined, // modal input needs px-2 and search inputs by default (px-3)
      customWidth = '',
      shouldNotCapitalize = false,
      shouldFocus = false,
      dataTestId = undefined,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef(null);

    useEffect(() => {
      if (shouldFocus && internalRef.current) {
        internalRef.current.focus();
      }
    }, [shouldFocus]);

    useImperativeHandle(ref, () => internalRef.current);

    const onChange = (e) => {
      setValue(e.target?.value || e.currentTarget?.value);
    };
    /**
     * Handle Enter key pressed
     */
    const handleEnterKeyPressed = (e) => {
      if (onKeyEnterPressed && e.code === 'Enter') {
        onKeyEnterPressed();
      }
    };

    // to position error message inside input container if errorMessageClass is 'absolute' relativePosition is needed
    const relativePosition = errorMessageClass === 'absolute' ? 'relative' : '';
    const borderClass = `${isValid ? borderColor : ' border-vamos-red'}`;

    const backgroundColor = `${isDisabled ? 'bg-vamos-blue-gray bg-opacity-60' : 'bg-vamos-white'}`;

    const placeholderClasses = `placeholder-vamos-gray ${isDisabled && 'placeholder-opacity-60'}`;

    const labelClasses = customLabelClasses || `${isLabelBeside ? `${labelBesideLabelClasses || 'w-2/5 font-bold text-base'} ${!isRequired && 'mb-0 '}` : `mb-0 ${labelTextSize || 'text-sm font-medium'}`} ${isDisabled ? 'text-vamos-blue-gray' : 'text-vamos-black'} font-sans`;

    const inputClasses = customInputClasses || `${isLabelBeside && !isRequired
      ? `${inputHeight || 'h-9'} ${customWidth || 'w-full'}`
      : `${inputHeight || 'h-8'} ${customWidth || 'w-full'}`} 
    border-solid border ${borderClass} rounded-md ${backgroundColor} 
    focus:border-vamos-black outline-none ${inputTextPadding || 'px-3'} 
    focus:ring-0 font-normal text-vamos-black ${placeholderClasses} ${className || ''}`;
    const icon = customIcon || <Icon name="required_field" className={`ml-1  ${asteriskWidth || 'w-3'}`} />;

    // render error message if any
    const renderError = () => {
      if (customErrorComponent) {
        return customErrorComponent;
      }
      return (
        <h4 type="invalid" className={`${errorMessageClass || ''} ${errorMessageFontClasses || `text-vamos-red font-normal text-sm ${errorMessageZIndexClass}`.trim()}`}>
          {isRequired && (value == null || value === '')
            && (
              <div className="flex">
                {isAlertIcon && <Icon name="alert" />} {errorMessage}
              </div>
            )}
          {validationError && validationError}
        </h4>
      );
    };

    // conditionally render error message
    const isErrored = (isRequired && errorMessage && (value == null || value === '')) || !!validationError;

    return (
      <div
        {...props}
        className={`${isLabelBeside
          ? `flex ${!isRequired ? 'items-center' : ''} ${fontSize || 'text-base'} font-medium`
          : `${spaceY || 'space-y-2'} ${customWidth || 'w-full'} ${fontSize || 'text-base font-medium'}`} ${relativePosition}`}
      >
        {label && (
        <label className={`flex ${(isRequired && isLabelBeside) ? 'items-center h-9' : 'items-center'}  ${labelClasses}`} htmlFor={name}>
          {shouldNotCapitalize ? label : capitalizeFirstLetter(label)}
          {isRequired && (icon)}
        </label>
        )}
        <div className={isLabelBeside ? `${labelBesideInputClasses || `w-3/5 ${spaceY || 'space-y-2'}`}` : `${spaceY || 'space-y-2'}`}>
          <input
            aria-required={isRequired}
            aria-invalid={isErrored}
            aria-label={label || name}
            className={`
              ${isRequired && (value == null || value === '') && 'border-vamos-red'}
              ${inputClasses}
            `}
            type={type}
            name={name}
            value={value}
            ref={internalRef}
            required={isRequired}
            placeholder={placeholder}
            onChange={onChange}
            onKeyDownCapture={handleEnterKeyPressed}
            style={style}
            id={name}
            disabled={isDisabled}
            defaultValue={defaultValue}
            data-testid={dataTestId}
          />
          {isErrored && renderError()}
        </div>
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';

TextInput.propTypes = {
  label: PropTypes.string,
  labelTextSize: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  setValue: PropTypes.func,
  type: PropTypes.string,
  isRequired: PropTypes.bool,
  asteriskWidth: PropTypes.string,
  errorMessage: PropTypes.string,
  onKeyEnterPressed: PropTypes.func,
  placeholder: PropTypes.string,
  isValid: PropTypes.bool,
  className: PropTypes.string,
  borderColor: PropTypes.string,
  isLabelBeside: PropTypes.bool,
  style: PropTypes.object,
  customErrorComponent: PropTypes.node,
  customLabelClasses: PropTypes.string,
  customInputClasses: PropTypes.string,
  customIcon: PropTypes.node,
  spaceY: PropTypes.string,
  fontSize: PropTypes.string,
  inputHeight: PropTypes.string,
  labelBesideLabelClasses: PropTypes.string,
  labelBesideInputClasses: PropTypes.string,
  isDisabled: PropTypes.bool,
  validationError: PropTypes.string,
  errorMessageClass: PropTypes.string,
  defaultValue: PropTypes.string,
  isAlertIcon: PropTypes.bool,
  errorMessageFontClasses: PropTypes.string,
  errorMessageZIndexClass: PropTypes.string,
  inputTextPadding: PropTypes.string,
  customWidth: PropTypes.string,
  shouldNotCapitalize: PropTypes.bool,
  dataTestId: PropTypes.string,
  shouldFocus: PropTypes.bool,
};

export default TextInput;
